import { supabase } from './supabase';
import { BRANDING, isForfettario } from './branding';
import type { Database } from '../types/database.types';
import { computeMarcaDaBollo } from './noVatClause';

type InvoiceRow = Database['public']['Tables']['invoices']['Row'];
type InvoiceInsert = Database['public']['Tables']['invoices']['Insert'];
type InvoiceItemInsert = Database['public']['Tables']['invoice_items']['Insert'];

export type InvoiceStatus = 'draft' | 'issued' | 'cancelled';
export type DocumentType = 'invoice' | 'invoice_with_delivery' | 'proforma' | 'ddt' | 'credit_note';

export const getDocumentTypeLabel = (type: DocumentType | string): string => {
  switch (type) {
    case 'invoice':
      return 'Fattura';
    case 'invoice_with_delivery':
      return 'Fattura Accompagnatoria';
    case 'proforma':
      return 'Fattura Pro Forma';
    case 'ddt':
      return 'DDT';
    case 'credit_note':
      return 'Nota di Credito';
    default:
      return String(type);
  }
};

export interface InvoiceItemInput {
  product_id: string | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

export interface InvoicePayload {
  customer_id: string;
  order_id?: string | null;
  invoice_date: string; // ISO yyyy-mm-dd
  payment_terms?: string | null;
  notes?: string | null;
  status: InvoiceStatus;
  document_type: DocumentType;
  items: InvoiceItemInput[];
  // Regime forfettario (assenza IVA L. 190/2014)
  no_vat_forfettario?: boolean;
  no_vat_clause_text?: string | null;
  iban?: string | null;
  causale?: string | null;
  // Campi specifici per DDT e Fattura Accompagnatoria
  carrier_id?: string | null;
  recipient_name?: string | null;
  recipient_address?: string | null;
  destination_address?: string | null;
  transport_date?: string | null; // ISO yyyy-mm-dd
  numero_colli?: number | null;
  package_type_id?: string | null;
  peso?: number | null;
  // Campi specifici per Nota di Credito
  reference_invoice_id?: string | null;
  credit_note_reason?: string | null;
  // Tracciabilità Offerta → Fattura e Acconto → Saldo
  price_list_id?: string | null;
  parent_invoice_id?: string | null;
  deposit_percentage?: number | null;
  // Periodo di abbonamento fatturato (solo bozze: vedi subscriptionInvoiceUtils)
  subscription_id?: string | null;
  subscription_period_start?: string | null;
  subscription_period_end?: string | null;
}

export interface CalculatedInvoiceTotals {
  subtotal_amount: number;
  discount_amount: number;
  taxable_amount: number;
  tax_amount: number;
  total_amount: number;
  marca_da_bollo_amount: number;
  items: Omit<InvoiceItemInsert, 'invoice_id'>[];
}

export const calculateInvoiceTotals = (
  items: InvoiceItemInput[],
  noVatForfettario: boolean = false
): CalculatedInvoiceTotals => {
  let subtotal = 0;
  let discount = 0;
  let taxable = 0;
  let tax = 0;

  const mappedItems: Omit<InvoiceItemInsert, 'invoice_id'>[] = items.map((item) => {
    const effectiveVatRate = noVatForfettario ? 0 : item.vat_rate;
    const rowSubtotal = item.quantity * item.unit_price;
    const rowDiscount = rowSubtotal * (item.discount_percentage / 100);
    const rowTaxable = rowSubtotal - rowDiscount;
    const rowTax = rowTaxable * (effectiveVatRate / 100);
    const rowTotal = rowTaxable + rowTax;

    subtotal += rowSubtotal;
    discount += rowDiscount;
    taxable += rowTaxable;
    tax += rowTax;

    return {
      product_id: item.product_id,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: effectiveVatRate,
      vat_amount: rowTax,
      line_total: rowTotal,
    };
  });

  const marcaDaBollo = computeMarcaDaBollo(taxable, noVatForfettario);

  return {
    subtotal_amount: Number(subtotal.toFixed(2)),
    discount_amount: Number(discount.toFixed(2)),
    taxable_amount: Number(taxable.toFixed(2)),
    tax_amount: Number(tax.toFixed(2)),
    total_amount: Number((taxable + tax + marcaDaBollo).toFixed(2)),
    marca_da_bollo_amount: marcaDaBollo,
    items: mappedItems,
  };
};

/**
 * Documenti che nascono su Fatture in Cloud, che e' l'unico a numerarli.
 *
 * Sono i documenti fiscali: quelli che prendono un numero di protocollo e che
 * poi vanno allo SDI. Se li numerasse anche Pixel, prima o poi i due sistemi
 * assegnerebbero lo stesso numero a due documenti diversi.
 *
 * Restano fuori:
 * - la pro forma, che non e' un documento fiscale e nasce sempre come bozza:
 *   mandarla su Fatture in Cloud brucerebbe un numero per niente;
 * - il DDT, che SLAB non emette da Fatture in Cloud (la sincronizzazione
 *   notturna infatti non scarica i documenti di trasporto), quindi la sua
 *   numerazione resta in Pixel e non puo' scontrarsi con niente.
 */
const NASCE_SU_FATTURE_IN_CLOUD: readonly DocumentType[] = [
  'invoice',
  'invoice_with_delivery',
  'credit_note',
];

/** Una bozza si può emettere su Fatture in Cloud se è di un tipo che nasce
 *  lì e non è già partita. */
export const canIssueDraftOnFattureInCloud = (invoice: {
  status: string;
  document_type: string;
  fic_id?: number | null;
}): boolean =>
  invoice.status === 'draft' &&
  !invoice.fic_id &&
  NASCE_SU_FATTURE_IN_CLOUD.includes(invoice.document_type as DocumentType);

/**
 * Mappa il tipo di documento al prefisso per la numerazione locale.
 * Vale solo per i documenti che Pixel numera ancora: vedi
 * NASCE_SU_FATTURE_IN_CLOUD.
 */
const getDocumentPrefix = (documentType: DocumentType): string => {
  switch (documentType) {
    case 'ddt':
      return 'DDT'; // Documento di Trasporto
    case 'proforma':
      return 'PF'; // Fattura pro forma
    default:
      return 'F';
  }
};

/**
 * Genera il numero documento chiamando la RPC `next_invoice_number`.
 *
 * ATTENZIONE: da usare solo per i documenti che Pixel numera ancora (DDT).
 * Per fatture, fatture accompagnatorie e note di credito numera Fatture in
 * Cloud: chiamare questa funzione per loro rimetterebbe in piedi proprio la
 * doppia numerazione che si e' voluta togliere.
 */
export const generateInvoiceNumber = async (
  invoiceDate: string,
  documentType: DocumentType = 'ddt'
): Promise<string | null> => {
  if (NASCE_SU_FATTURE_IN_CLOUD.includes(documentType)) {
    throw new Error(
      `${getDocumentTypeLabel(documentType)}: il numero lo assegna Fatture in Cloud, non Pixel.`
    );
  }

  const year = new Date(invoiceDate).getFullYear();
  const prefix = getDocumentPrefix(documentType);

  const { data, error } = await supabase.rpc('next_invoice_number', {
    p_year: year,
    p_prefix: prefix,
  });

  if (error) {
    console.error('Errore generazione numero documento:', error);
    // In caso di errore, restituiamo null così il documento rimane senza numero
    return null;
  }

  return data as string;
};

/**
 * supabase-js, quando una edge function risponde con un codice diverso da 2xx,
 * espone solo "Edge Function returned a non-2xx status code": il messaggio
 * vero sta nel corpo della risposta, che va letto a parte.
 */
const leggiErroreFunzione = async (error: unknown, fallback: string): Promise<string> => {
  const ctx = (error as { context?: { json?: () => Promise<unknown> } })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { errore?: string; error?: string; dettaglio?: string };
      const testa = body?.errore || body?.error;
      if (typeof testa === 'string' && testa.trim()) {
        return body.dettaglio ? `${testa}\n\n${body.dettaglio}` : testa;
      }
    } catch {
      // corpo non JSON: resta il messaggio generico
    }
  }
  return (error as { message?: string })?.message || fallback;
};

/**
 * Le coordinate per il bonifico dentro la fattura elettronica: senza, il
 * cliente riceve un documento che non dice dove pagare. Fatture in Cloud non
 * le compila da sola sui documenti creati via API.
 */
const datiBanca = (iban: string | null | undefined) => ({
  nome: BRANDING.banca || null,
  iban: iban || null,
  beneficiario: BRANDING.ragioneSociale,
});

/**
 * Legge la risposta di fic-crea-documento e riporta la riga salvata in Pixel.
 * Se la funzione dice che il documento non è nato, lancia l'errore con il suo
 * messaggio.
 */
const leggiDocumentoCreato = async (
  data: unknown,
  error: unknown,
  fallback: string
): Promise<InvoiceRow> => {
  if (error) {
    throw new Error(await leggiErroreFunzione(error, fallback));
  }

  const esito = data as { ok?: boolean; errore?: string; dettaglio?: string; invoice_id?: string };

  if (!esito?.ok || !esito.invoice_id) {
    const testa = esito?.errore || fallback;
    throw new Error(esito?.dettaglio ? `${testa}\n\n${esito.dettaglio}` : testa);
  }

  const { data: invoice, error: errLettura } = await supabase
    .from('invoices')
    .select('*')
    .eq('id', esito.invoice_id)
    .single();

  if (errLettura || !invoice) {
    // Il documento ESISTE, su Fatture in Cloud e in Pixel: e' solo la rilettura
    // ad essere fallita. Va detto, o l'utente lo rifarebbe creandone due.
    throw new Error(
      'Documento creato e numerato correttamente, ma non è stato possibile rileggerlo. ' +
        'Aggiorna la pagina prima di rifarlo: rifarlo creerebbe un secondo documento.'
    );
  }

  return invoice as InvoiceRow;
};

/**
 * Emette su Fatture in Cloud una bozza già salvata in Pixel: la stessa riga
 * diventa la fattura emessa, con il numero assegnato da loro e la data di
 * oggi. Righe, cliente e importi li legge la funzione dal database, non da qui.
 */
export const issueDraftOnFattureInCloud = async (invoiceId: string): Promise<InvoiceRow> => {
  const { data, error } = await supabase.functions.invoke('fic-crea-documento', {
    body: {
      bozza_id: invoiceId,
      // Vale solo se la bozza non ha un IBAN suo.
      banca: datiBanca(BRANDING.iban),
    },
  });
  return leggiDocumentoCreato(data, error, 'Fatture in Cloud non ha emesso la bozza.');
};

/**
 * Crea il documento su Fatture in Cloud e ne riporta la riga salvata in Pixel.
 * Il numero lo assegna loro e torna indietro nella risposta.
 */
const createInvoiceOnFattureInCloud = async (
  payload: InvoicePayload,
  noVatForfettario: boolean
): Promise<InvoiceRow> => {
  const { data, error } = await supabase.functions.invoke('fic-crea-documento', {
    body: {
      cliente_id: payload.customer_id,
      data: payload.invoice_date,
      tipo: payload.document_type,
      note: payload.notes ?? null,
      iban: payload.iban ?? null,
      banca: payload.iban ? datiBanca(payload.iban) : null,
      righe: payload.items.map((item) => ({
        descrizione: item.description,
        quantita: item.quantity,
        prezzo_unitario: item.unit_price,
        aliquota_iva: noVatForfettario ? 0 : item.vat_rate,
        sconto_percentuale: item.discount_percentage,
        unita: item.unit ?? null,
        prodotto_id: item.product_id ?? null,
      })),
      // Campi che vivono solo in Pixel: Fatture in Cloud non li conosce, ma
      // servono alle stampe e alla tracciabilità offerta → fattura.
      extra: {
        order_id: payload.order_id ?? null,
        payment_terms: payload.payment_terms ?? null,
        payment_status: 'pending',
        no_vat_forfettario: noVatForfettario,
        no_vat_clause_text: payload.no_vat_clause_text ?? null,
        causale: payload.causale ?? null,
        carrier_id: payload.carrier_id ?? null,
        recipient_name: payload.recipient_name ?? null,
        recipient_address: payload.recipient_address ?? null,
        destination_address: payload.destination_address ?? null,
        transport_date: payload.transport_date ?? null,
        numero_colli: payload.numero_colli ?? null,
        package_type_id: payload.package_type_id ?? null,
        peso: payload.peso ?? null,
        reference_invoice_id: payload.reference_invoice_id ?? null,
        credit_note_reason: payload.credit_note_reason ?? null,
        price_list_id: payload.price_list_id ?? null,
        parent_invoice_id: payload.parent_invoice_id ?? null,
        deposit_percentage: payload.deposit_percentage ?? null,
      },
    },
  });

  return leggiDocumentoCreato(data, error, 'Fatture in Cloud non ha creato il documento.');
};

export const createInvoice = async (payload: InvoicePayload): Promise<InvoiceRow> => {
  if (!payload.items || payload.items.length === 0) {
    throw new Error('Il documento deve contenere almeno una riga.');
  }

  // Recupera l'utente corrente
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Utente non autenticato. Devi essere loggato per creare documenti.');
  }

  // Il documento nasce senza IVA solo se l'azienda e' davvero in regime
  // forfettario (codice RF19 nella configurazione). Prima era forzato a true:
  // per una s.r.l. in regime ordinario ogni fattura sarebbe nata senza IVA e
  // con la clausola della L. 190/2014 in calce.
  const noVatForfettario = payload.no_vat_forfettario ?? isForfettario();

  // Un documento fiscale emesso nasce su Fatture in Cloud, che lo numera.
  // Le bozze restano in Pixel: non hanno numero e non esistono per il fisco,
  // quindi non c'e' niente da far assegnare a nessuno.
  if (payload.status === 'issued' && NASCE_SU_FATTURE_IN_CLOUD.includes(payload.document_type)) {
    return createInvoiceOnFattureInCloud(payload, noVatForfettario);
  }

  const { items, ...totals } = calculateInvoiceTotals(payload.items, noVatForfettario);

  // Numerazione locale: resta solo per i DDT, che Fatture in Cloud non emette.
  let invoiceNumber: string | null = null;
  if (payload.status === 'issued' && payload.document_type === 'ddt') {
    invoiceNumber = await generateInvoiceNumber(payload.invoice_date, payload.document_type);
  }

  const insertData = {
    customer_id: payload.customer_id,
    order_id: payload.order_id ?? null,
    invoice_date: payload.invoice_date,
    payment_terms: payload.payment_terms ?? null,
    payment_due_date: null,
    payment_status: 'pending',
    status: payload.status,
    document_type: payload.document_type,
    notes: payload.notes ?? null,
    invoice_number: invoiceNumber ?? undefined,
    subtotal_amount: totals.subtotal_amount,
    discount_amount: totals.discount_amount,
    taxable_amount: totals.taxable_amount,
    tax_amount: totals.tax_amount,
    total_amount: totals.total_amount,
    currency: 'EUR',
    no_vat_forfettario: noVatForfettario,
    no_vat_clause_text: payload.no_vat_clause_text ?? null,
    marca_da_bollo_amount: totals.marca_da_bollo_amount,
    iban: payload.iban ?? null,
    causale: payload.causale ?? null,
    // Campi specifici per DDT e Fattura Accompagnatoria
    carrier_id: payload.carrier_id ?? null,
    recipient_name: payload.recipient_name ?? null,
    recipient_address: payload.recipient_address ?? null,
    destination_address: payload.destination_address ?? null,
            transport_date: payload.transport_date ?? null,
            numero_colli: payload.numero_colli ?? null,
            package_type_id: payload.package_type_id ?? null,
            peso: payload.peso ?? null,
    // Campi specifici per Nota di Credito
    reference_invoice_id: payload.reference_invoice_id ?? null,
    credit_note_reason: payload.credit_note_reason ?? null,
    // Tracciabilità Offerta → Fattura e Acconto → Saldo
    price_list_id: payload.price_list_id ?? null,
    parent_invoice_id: payload.parent_invoice_id ?? null,
    deposit_percentage: payload.deposit_percentage ?? null,
    // Periodo di abbonamento: il database rifiuta un secondo documento sullo
    // stesso periodo (indice uq_invoices_subscription_period).
    subscription_id: payload.subscription_id ?? null,
    subscription_period_start: payload.subscription_period_start ?? null,
    subscription_period_end: payload.subscription_period_end ?? null,
    created_at: undefined,
    updated_at: undefined,
    created_by: user.id,
    id: undefined as any,
  } as any;

  let { data: invoice, error: invoiceError } = await supabase
    .from('invoices')
    .insert(insertData)
    .select('*')
    .single();

  // Fallback: se la colonna document_type non esiste, riprova senza
  if (invoiceError) {
    const err = invoiceError as any;
    if (
      err?.code === 'PGRST204' ||
      String(err?.message || '').toLowerCase().includes('document_type') ||
      String(err?.message || '').includes('column') && String(err?.message || '').includes('document_type')
    ) {
      console.warn('Campo document_type non trovato nel database, tentativo senza campo...');
      const { document_type, ...fallbackData } = insertData;
      const result = await supabase
        .from('invoices')
        .insert(fallbackData)
        .select('*')
        .single();
      
      if (result.error) {
        console.error('Errore creazione fattura (anche senza document_type):', result.error);
        throw new Error(
          'Errore creazione documento. Assicurati di aver eseguito lo script SQL ADD_DOCUMENT_TYPES_TO_INVOICES.sql sul database. Dettagli: ' + 
          (result.error.message || 'Errore sconosciuto')
        );
      }
      invoice = result.data;
      invoiceError = null;
    } else {
      console.error('Errore creazione fattura:', invoiceError);
      throw invoiceError;
    }
  }

  if (!invoice) {
    throw new Error('Errore creazione documento: nessun dato restituito');
  }

  // Prepara gli item per l'inserimento, includendo solo i campi necessari
  const itemsWithInvoice: InvoiceItemInsert[] = items.map((item) => {
    const cleanItem: any = {
      invoice_id: invoice.id,
      product_id: item.product_id ?? null,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit ?? null,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: item.vat_rate,
      vat_amount: item.vat_amount,
      line_total: item.line_total,
    };
    
    // Includi order_item_id solo se presente (è opzionale)
    if (item.order_item_id !== null && item.order_item_id !== undefined) {
      cleanItem.order_item_id = item.order_item_id;
    }
    
    return cleanItem as InvoiceItemInsert;
  });

  const { error: itemsError } = await supabase
    .from('invoice_items')
    .insert(itemsWithInvoice);

  if (itemsError) {
    console.error('Errore creazione righe fattura:', itemsError);
    throw itemsError;
  }

  return invoice;
};




